import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { SEOHead } from "@/components/SEOHead";

type Plan = {
  id: string;
  name: string;
  price: number;
  description: string;
  is_popular: boolean;
  display_order: number;
  features: string[];
};

type Addon = {
  id: string;
  name: string;
  price: number;
  description: string | null;
};

export default function PricingNew() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [addons, setAddons] = useState<Addon[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingPlanId, setProcessingPlanId] = useState<string | null>(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchPricingData();
  }, []);

  const handleChoosePlan = async (plan: Plan) => {
    try {
      if (!user) {
        toast.error('Please log in to purchase a plan');
        navigate('/auth');
        return;
      }

      setProcessingPlanId(plan.id);
      
      // Call edge function to create Mollie checkout session
      const { data, error } = await supabase.functions.invoke('create-site-plan-checkout', {
        body: {
          planId: plan.id,
        },
      });

      if (error) throw error;

      // Open checkout in new tab
      if (data?.url) {
        window.open(data.url, '_blank');
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (error: any) {
      console.error('Error creating checkout:', error);
      
      // Handle specific errors
      if (error.message?.includes('not associated with a PTA')) {
        toast.error('You must be a member of a PTA to purchase a plan');
      } else {
        toast.error('Failed to start checkout. Please try again.');
      }
    } finally {
      setProcessingPlanId(null);
    }
  };

  const fetchPricingData = async () => {
    try {
      // Fetch plans
      const { data: plansData, error: plansError } = await supabase
        .from("pricing_plans")
        .select("*")
        .order("display_order", { ascending: true });

      if (plansError) throw plansError;

      // Fetch features for each plan
      const plansWithFeatures = await Promise.all(
        (plansData || []).map(async (plan) => {
          const { data: featuresData } = await supabase
            .from("plan_features")
            .select("*")
            .eq("plan_id", plan.id)
            .order("feature_order", { ascending: true });

          return {
            ...plan,
            features: featuresData?.map((f) => f.feature_name) || [],
          };
        })
      );

      setPlans(plansWithFeatures);

      // Fetch addons
      const { data: addonsData, error: addonsError } = await supabase
        .from("pricing_addons")
        .select("*")
        .order("name", { ascending: true });

      if (addonsError) throw addonsError;
      setAddons(addonsData || []);
    } catch (error) {
      console.error("Error fetching pricing data:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const gridCols = plans.length === 1 ? "md:grid-cols-1 max-w-2xl" : plans.length === 2 ? "md:grid-cols-2 max-w-5xl" : "md:grid-cols-3 max-w-7xl";

  return (
    <div className="space-y-16 animate-fade-in py-8">
      <SEOHead slug="pricing" />
      {/* Hero Section */}
      <div className="text-center space-y-4">
        <h1 className="text-4xl md:text-5xl font-bold text-foreground">Find the Perfect Plan for Your PTA</h1>
        <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
          Simple, transparent pricing to help you manage and grow your PTA with ease.
        </p>
      </div>

      {/* Pricing Cards */}
      <div className={`grid ${gridCols} gap-8 mx-auto`}>
        {plans.map((plan) => (
          <Card 
            key={plan.id} 
            className={`shadow-card relative flex flex-col ${plan.is_popular ? 'border-primary border-2' : ''}`}
          >
            {plan.is_popular && (
              <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                <span className="bg-primary text-primary-foreground px-4 py-1 rounded-full text-sm font-medium">
                  MOST POPULAR
                </span>
              </div>
            )}
            <CardHeader>
              <CardTitle className={`text-2xl ${plan.is_popular ? 'text-primary' : ''}`}>
                {plan.name}
              </CardTitle>
              <CardDescription>{plan.description}</CardDescription>
              <div className="pt-4">
                <span className={`text-5xl font-bold ${plan.is_popular ? 'text-primary' : 'text-foreground'}`}>
                  £{plan.price.toFixed(2)}
                </span>
                <span className="text-muted-foreground">/year</span>
              </div>
            </CardHeader>
            <CardContent className="space-y-6 flex flex-col flex-grow">
              <div className="space-y-3 flex-grow">
                {plan.features.map((feature, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-muted-foreground">{feature}</span>
                  </div>
                ))}
              </div>
              <div className="space-y-2">
                <Button 
                  className="w-full" 
                  size="lg"
                  onClick={() => handleChoosePlan(plan)}
                  disabled={processingPlanId === plan.id}
                >
                  {processingPlanId === plan.id ? 'Processing...' : `Choose ${plan.name}`}
                </Button>
                <Button className="w-full" variant="outline" size="lg" asChild>
                  <Link to="/auth">Start 28-day trial</Link>
                </Button>
                <p className="text-xs text-center text-muted-foreground">No credit card required for trial.</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Free Sponsorship CTA */}
      <Card className="shadow-elevated max-w-4xl mx-auto border-2 border-primary/20 bg-gradient-to-br from-background to-muted/30">
        <CardContent className="py-12 px-8">
          <div className="text-center space-y-6">
            <h2 className="text-4xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
              Want PTA Hub for Free?
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Get your PTA membership fully sponsored by local businesses in your community. 
              It's completely free for your PTA, and businesses get valuable exposure while supporting education.
            </p>
            <Button asChild size="lg" className="mt-4">
              <Link to="/sponsorship">
                Learn About Sponsorship
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Add-ons Section */}
      {addons.length > 0 && (
        <Card className="shadow-card max-w-5xl mx-auto">
          <CardHeader className="text-center">
            <CardTitle className="text-3xl">Available Add-ons</CardTitle>
            <CardDescription className="text-base">
              Supercharge your plan with powerful extra features.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {addons.map((addon) => (
                <div key={addon.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <span className="font-medium">{addon.name}</span>
                    {addon.description && (
                      <p className="text-sm text-muted-foreground mt-1">{addon.description}</p>
                    )}
                  </div>
                  <span className="text-primary font-bold">
                    £{addon.price.toFixed(2)}<span className="text-muted-foreground font-normal">/yr</span>
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
